import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, paid, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async (attempt = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('paid');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
    } catch (error) {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
      } else {
        setStatus('failed');
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && user) {
      pollPaymentStatus();
    }
  }, [sessionId, user, pollPaymentStatus]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center py-12 px-4">
        <Card className="max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">Invalid Session</h2>
            <p className="text-[#4A4A4A] mb-6">No payment session found.</p>
            <Button onClick={() => navigate('/subscribe')} className="bg-[#E05D3A] hover:bg-[#C94F2E]">
              Back to Subscribe
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center py-12 px-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full text-center">
        <CardContent className="pt-8 pb-8">
          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 className="w-16 h-16 animate-spin text-[#E05D3A] mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">Confirming Payment</h2>
              <p className="text-[#4A4A4A]">Please wait while we verify your payment...</p>
              <p className="text-xs text-[#4A4A4A] mt-2">Attempt {attempts + 1}</p>
            </div>
          )}

          {status === 'paid' && (
            <div data-testid="payment-confirmed">
              <CheckCircle2 className="w-16 h-16 text-[#2C402E] mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">Payment Successful!</h2>
              <p className="text-[#4A4A4A] mb-4">Your subscription has been activated.</p>
              {paymentData && (
                <div className="bg-[#FAF6F0] rounded-xl p-4 mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-[#4A4A4A]">Plan</span>
                    <Badge className="bg-[#E05D3A]/10 text-[#E05D3A] capitalize">{paymentData.plan_type}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-[#4A4A4A]">Amount Paid</span>
                    <span className="font-bold text-[#2C402E]">&pound;{paymentData.amount}</span>
                  </div>
                </div>
              )}
              <Button onClick={() => navigate('/subscribe')} className="bg-[#E05D3A] hover:bg-[#C94F2E] w-full">
                View My Subscription
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <div data-testid="payment-failed">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">
                {status === 'timeout' ? 'Verification Timeout' : 'Payment Issue'}
              </h2>
              <p className="text-[#4A4A4A] mb-6">
                {status === 'timeout'
                  ? 'We could not verify your payment in time. If you were charged, your subscription will be activated shortly.'
                  : 'Something went wrong with payment verification.'}
              </p>
              <Button onClick={() => navigate('/subscribe')} className="bg-[#E05D3A] hover:bg-[#C94F2E]">
                Back to Subscribe
              </Button>
            </div>
          )}

          {status === 'expired' && (
            <div data-testid="payment-expired">
              <XCircle className="w-16 h-16 text-[#4A4A4A] mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">Session Expired</h2>
              <p className="text-[#4A4A4A] mb-6">This payment session has expired. Please try again.</p>
              <Button onClick={() => navigate('/subscribe')} className="bg-[#E05D3A] hover:bg-[#C94F2E]">
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
